/**
 * Extract references from an LLM-generated markdown text and convert inline [1] [2] citations
 * into clickable links.
 *
 * Supports these reference styles at the bottom of the text:
 *   - "[1] https://..."
 *   - "[1]: https://..."
 *   - "1. https://..."
 *   - "1) https://..."
 *
 * Returns { html_ready_md, references: [{ num, url, title }] }
 * If no references detected, returns the original text unchanged.
 */
export function extractCitations(markdown) {
  if (!markdown || typeof markdown !== "string") return { markdown, references: [] };

  // Match reference lines: [N] URL  |  [N]: URL  |  N. URL  |  N) URL
  const refPatterns = [
    /^\s*\[(\d+)\]:?\s+(https?:\/\/\S+)(?:\s+(.*))?$/gm,
    /^\s*(\d+)[.)]\s+(https?:\/\/\S+)(?:\s+(.*))?$/gm,
  ];

  const refs = new Map(); // num -> {url, title}
  for (const pattern of refPatterns) {
    let m;
    while ((m = pattern.exec(markdown)) !== null) {
      const num = m[1];
      const url = m[2];
      const title = (m[3] || "").trim() || null;
      if (!refs.has(num)) refs.set(num, { url, title });
    }
  }

  if (refs.size === 0) return { markdown, references: [] };

  // Replace inline [N] (that isn't already a link) with markdown link syntax
  const inlineCitationRegex = /\[(\d+)\](?!\()/g;
  const patchedMd = markdown.replace(inlineCitationRegex, (match, num) => {
    const ref = refs.get(num);
    if (!ref) return match;
    return `[[${num}]](${ref.url})`;
  });

  const references = Array.from(refs.entries())
    .map(([num, r]) => ({ num: parseInt(num, 10), url: r.url, title: r.title }))
    .sort((a, b) => a.num - b.num);

  return { markdown: patchedMd, references };
}

/**
 * React component that renders a compact clickable list of references extracted from LLM output.
 * Place it under a ReactMarkdown block.
 */
export function ReferenceList({ references, className = "" }) {
  if (!references || !references.length) return null;
  return (
    <div className={`mt-4 pt-3 border-t ${className}`} data-testid="ia-references">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Referencias comprobables ({references.length})</div>
      <div className="space-y-1">
        {references.map((r) => (
          <a
            key={r.num}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 text-xs hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded p-1.5 transition-colors group"
            data-testid={`ia-reference-${r.num}`}
          >
            <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-[10px] flex-shrink-0 mt-0.5">{r.num}</span>
            <span className="flex-1 min-w-0">
              {r.title && <div className="font-medium text-slate-800 dark:text-slate-100 truncate group-hover:text-emerald-700">{r.title}</div>}
              <div className={`text-[10px] text-emerald-700 truncate group-hover:underline ${!r.title ? "font-medium" : ""}`}>{r.url}</div>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
